// Reading an .xlsx in the browser (design 02 12.1): sheets, values,
// formulas (shared ones spread out), number formats, bold/italic/underline,
// colours, alignment, column widths, frozen panes and defined names. What
// is not taken (charts, pivots, macros, conditional formats, ...) is listed
// in `skipped` so the person is told.

import { fromFileFormula } from "./formula";
import {
  CELL_INPUT_MAX_BYTES,
  CELLS_MAX,
  COL_WIDTH_MAX,
  COL_WIDTH_MIN,
  definedNameValid,
  FROZEN_COLS_MAX,
  FROZEN_ROWS_MAX,
  NAMES_MAX,
  NF_MAX,
  numberInput,
  parseCell,
  SHEETS_MAX,
  sheetNameValid,
  textInput,
  utf8Length,
  type XBook,
  type XCell,
  type XName,
  type XSheet,
  type XStyle,
} from "./model";
import { child, children, deepText, parseXml, type XmlNode } from "./xml";
import { readZip } from "./zip";

export class XlsxError extends Error {}

export interface ReadOptions {
  /**
   * A shared formula moved from its first cell by (dRow, dCol), as Excel
   * would (relative references move, $absolute ones stay). The engine's
   * calc_translate. Without it, cells of shared formulas keep their values.
   */
  translate?: (formula: string, dRow: number, dCol: number) => string | null;
}

const REL_OFFICE = "http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument";
const REL_STRICT_OFFICE = "http://purl.oclc.org/ooxml/officeDocument/relationships/officeDocument";

const dec = new TextDecoder("utf-8");

class Parts {
  constructor(private files: Map<string, Uint8Array>) {}

  has(path: string): boolean {
    return this.files.has(path);
  }

  xml(path: string): XmlNode | undefined {
    const data = this.files.get(path);
    if (data === undefined) return undefined;
    try {
      return parseXml(dec.decode(data));
    } catch (e) {
      throw new XlsxError(`${path}: ${(e as Error).message}`);
    }
  }

  names(): string[] {
    return [...this.files.keys()];
  }
}

function folderOf(path: string): string {
  const i = path.lastIndexOf("/");
  return i < 0 ? "" : path.slice(0, i + 1);
}

function resolve(base: string, target: string): string {
  if (target.startsWith("/")) return target.slice(1);
  const parts = (folderOf(base) + target).split("/");
  const out: string[] = [];
  for (const p of parts) {
    if (p === "..") out.pop();
    else if (p !== "." && p !== "") out.push(p);
  }
  return out.join("/");
}

interface Rel {
  type: string;
  target: string;
  external: boolean;
}

function rels(parts: Parts, path: string): Map<string, Rel> {
  const relPath = folderOf(path) + "_rels/" + path.slice(folderOf(path).length) + ".rels";
  const root = parts.xml(relPath);
  const map = new Map<string, Rel>();
  for (const r of children(root, "Relationship")) {
    const external = r.attrs.TargetMode === "External";
    map.set(r.attrs.Id ?? "", {
      type: r.attrs.Type ?? "",
      target: external ? (r.attrs.Target ?? "") : resolve(path, r.attrs.Target ?? ""),
      external,
    });
  }
  return map;
}

// ---- colours ----

const INDEXED = [
  "000000", "FFFFFF", "FF0000", "00FF00", "0000FF", "FFFF00", "FF00FF", "00FFFF",
  "000000", "FFFFFF", "FF0000", "00FF00", "0000FF", "FFFF00", "FF00FF", "00FFFF",
  "800000", "008000", "000080", "808000", "800080", "008080", "C0C0C0", "808080",
  "9999FF", "993366", "FFFFCC", "CCFFFF", "660066", "FF8080", "0066CC", "CCCCFF",
  "000080", "FF00FF", "FFFF00", "00FFFF", "800080", "800000", "008080", "0000FF",
  "00CCFF", "CCFFFF", "CCFFCC", "FFFF99", "99CCFF", "FF99CC", "CC99FF", "FFCC99",
  "3366FF", "33CCCC", "99CC00", "FFCC00", "FF9900", "FF6600", "666699", "969696",
  "003366", "339966", "003300", "333300", "993300", "993366", "333399", "333333",
];

// Office's default theme, in Excel's theme-index order (lt1, dk1, lt2, dk2, accents)
const DEFAULT_THEME = ["FFFFFF", "000000", "E7E6E6", "44546A", "4472C4", "ED7D31", "A5A5A5", "FFC000", "5B9BD5", "70AD47"];

function readTheme(parts: Parts, path: string | undefined): string[] {
  const root = path === undefined ? undefined : parts.xml(path);
  const scheme = child(child(root, "themeElements"), "clrScheme");
  if (scheme === undefined) return DEFAULT_THEME;
  const pick = (name: string, fallback: string): string => {
    const node = child(scheme, name)?.children[0];
    return node?.attrs.lastClr ?? node?.attrs.val ?? fallback;
  };
  const order = ["lt1", "dk1", "lt2", "dk2", "accent1", "accent2", "accent3", "accent4", "accent5", "accent6"];
  return order.map((n, i) => pick(n, DEFAULT_THEME[i] ?? "000000"));
}

function tinted(hex: string, tint: number): string {
  if (tint === 0) return hex;
  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  let l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
    h /= 6;
  }
  l = tint < 0 ? l * (1 + tint) : l * (1 - tint) + tint;
  const hue = (p: number, q: number, t: number): number => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  let rr = l;
  let gg = l;
  let bb = l;
  if (s !== 0) {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    rr = hue(p, q, h + 1 / 3);
    gg = hue(p, q, h);
    bb = hue(p, q, h - 1 / 3);
  }
  const to = (v: number): string =>
    Math.round(Math.min(1, Math.max(0, v)) * 255)
      .toString(16)
      .padStart(2, "0");
  return (to(rr) + to(gg) + to(bb)).toUpperCase();
}

function colour(node: XmlNode | undefined, theme: string[]): string | undefined {
  if (node === undefined || node.attrs.auto === "1") return undefined;
  let hex: string | undefined;
  if (node.attrs.rgb !== undefined) {
    const v = node.attrs.rgb;
    hex = v.length === 8 ? v.slice(2) : v.length === 6 ? v : undefined;
  } else if (node.attrs.indexed !== undefined) {
    hex = INDEXED[Number(node.attrs.indexed)];
  } else if (node.attrs.theme !== undefined) {
    hex = theme[Number(node.attrs.theme)];
  }
  if (hex === undefined || !/^[0-9A-Fa-f]{6}$/.test(hex)) return undefined;
  hex = tinted(hex.toUpperCase(), Number(node.attrs.tint ?? 0) || 0);
  return "#" + hex.toLowerCase();
}

// ---- styles ----

export const BUILTIN_FORMATS: Record<number, string> = {
  1: "0",
  2: "0.00",
  3: "#,##0",
  4: "#,##0.00",
  9: "0%",
  10: "0.00%",
  11: "0.00E+00",
  12: "# ?/?",
  13: "# ??/??",
  14: "yyyy-mm-dd",
  15: "d-mmm-yy",
  16: "d-mmm",
  17: "mmm-yy",
  18: "h:mm AM/PM",
  19: "h:mm:ss AM/PM",
  20: "h:mm",
  21: "h:mm:ss",
  22: "yyyy-mm-dd h:mm",
  37: "#,##0 ;(#,##0)",
  38: "#,##0 ;[Red](#,##0)",
  39: "#,##0.00;(#,##0.00)",
  40: "#,##0.00;[Red](#,##0.00)",
  45: "mm:ss",
  46: "[h]:mm:ss",
  47: "mm:ss.0",
  48: "##0.0E+0",
  49: "@",
};

function readStyles(root: XmlNode | undefined, theme: string[], skipped: Set<string>): (XStyle | undefined)[] {
  if (root === undefined) return [];
  const formats = new Map<number, string>();
  for (const f of children(child(root, "numFmts"), "numFmt")) formats.set(Number(f.attrs.numFmtId), f.attrs.formatCode ?? "");
  const fonts = children(child(root, "fonts"), "font").map((f) => {
    const s: XStyle = {};
    const b = child(f, "b");
    if (b !== undefined && b.attrs.val !== "0" && b.attrs.val !== "false") s.b = true;
    const i = child(f, "i");
    if (i !== undefined && i.attrs.val !== "0" && i.attrs.val !== "false") s.i = true;
    const u = child(f, "u");
    if (u !== undefined && u.attrs.val !== "none") s.u = true;
    const c = colour(child(f, "color"), theme);
    if (c !== undefined && c !== "#000000") s.fg = c;
    return s;
  });
  const fills = children(child(root, "fills"), "fill").map((f) => {
    const p = child(f, "patternFill");
    if (p === undefined) {
      if (child(f, "gradientFill") !== undefined) skipped.add("그라데이션 채우기");
      return undefined;
    }
    if (p.attrs.patternType === undefined || p.attrs.patternType === "none" || p.attrs.patternType === "gray125")
      return undefined;
    return colour(child(p, "fgColor"), theme);
  });
  return children(child(root, "cellXfs"), "xf").map((xf) => {
    const s: XStyle = { ...(fonts[Number(xf.attrs.fontId ?? 0)] ?? {}) };
    const id = Number(xf.attrs.numFmtId ?? 0);
    const nf = id === 0 ? undefined : (formats.get(id) ?? BUILTIN_FORMATS[id]);
    if (nf !== undefined && nf !== "" && nf !== "General") {
      if ([...nf].length <= NF_MAX) s.nf = nf;
      else skipped.add("64자를 넘는 표시 형식");
    }
    const bg = fills[Number(xf.attrs.fillId ?? 0)];
    if (bg !== undefined) s.bg = bg;
    const al = child(xf, "alignment");
    const h = al?.attrs.horizontal;
    if (h === "left") s.al = "l";
    else if (h === "center" || h === "centerContinuous") s.al = "c";
    else if (h === "right") s.al = "r";
    if (al?.attrs.wrapText === "1" || al?.attrs.wrapText === "true") s.wrap = true;
    return Object.keys(s).length > 0 ? s : undefined;
  });
}

// ---- cells ----

// Excel writes characters XML cannot carry as _xHHHH_
function unescapeExcel(s: string): string {
  return s.includes("_x") ? s.replace(/_x([0-9A-Fa-f]{4})_/g, (_, h: string) => String.fromCharCode(parseInt(h, 16))) : s;
}

function excelColumnPx(width: number): number {
  // Excel: width in characters of the default font's digit (7 px for Calibri 11)
  const px = Math.trunc(((256 * width + Math.trunc(128 / 7)) / 256) * 7);
  return Math.min(COL_WIDTH_MAX, Math.max(COL_WIDTH_MIN, px));
}

function isoToSerial(s: string): number | null {
  const ms = Date.parse(s.endsWith("Z") || /[+-]\d\d:\d\d$/.test(s) ? s : s + "Z");
  return Number.isNaN(ms) ? null : ms / 86_400_000 + 25_569;
}

interface Shared {
  formula: string;
  row: number;
  col: number;
}

function readSheet(
  root: XmlNode,
  name: string,
  strings: string[],
  styles: (XStyle | undefined)[],
  opts: ReadOptions,
  skipped: Set<string>,
): XSheet {
  const sheet: XSheet = { name, cells: [], colWidths: {}, frozenRows: 0, frozenCols: 0 };
  const shared = new Map<string, Shared>();
  // array formulas (old Ctrl+Shift+Enter and dynamic ones): the other cells
  // of the range hold only cached values, which would block the spill here
  const arrays: { r0: number; c0: number; r1: number; c1: number }[] = [];
  const inArray = (row: number, col: number): boolean =>
    arrays.some((a) => row >= a.r0 && row <= a.r1 && col >= a.c0 && col <= a.c1 && !(row === a.r0 && col === a.c0));
  let maxCol = -1;
  let tooLong = false;
  for (const row of children(child(root, "sheetData"), "row")) {
    let nextCol = 0;
    const rowNum = row.attrs.r !== undefined ? Number(row.attrs.r) - 1 : NaN;
    for (const c of children(row, "c")) {
      let at = c.attrs.r !== undefined ? parseCell(c.attrs.r) : null;
      if (at === null) {
        if (Number.isNaN(rowNum)) continue;
        at = { row: rowNum, col: nextCol };
      }
      nextCol = at.col + 1;
      const t = c.attrs.t ?? "n";
      const v = child(c, "v")?.text;
      const f = child(c, "f");
      let input = "";
      if (f !== undefined && f.attrs.t === "array" && f.attrs.ref !== undefined) {
        const [a = "", b] = f.attrs.ref.split(":");
        const from = parseCell(a);
        const to = parseCell(b ?? a);
        if (from !== null && to !== null) arrays.push({ r0: from.row, c0: from.col, r1: to.row, c1: to.col });
      }
      const covered = f === undefined && inArray(at.row, at.col);
      if (f !== undefined) {
        let text = f.text;
        if (f.attrs.t === "shared" && f.attrs.si !== undefined) {
          if (text.trim() !== "") {
            shared.set(f.attrs.si, { formula: text, row: at.row, col: at.col });
          } else {
            const master = shared.get(f.attrs.si);
            const moved =
              master !== undefined && opts.translate !== undefined
                ? opts.translate(fromFileFormula(master.formula), at.row - master.row, at.col - master.col)
                : null;
            if (moved !== null) text = moved;
            else {
              text = "";
              skipped.add("공유 수식 일부(값으로 가져옴)");
            }
            if (text !== "") input = "=" + text;
          }
        }
        if (input === "" && text !== "") input = "=" + fromFileFormula(text);
      }
      if (input === "" && !covered) {
        if (t === "s") {
          const s = strings[Number(v)];
          if (s !== undefined && s !== "") input = textInput(s);
        } else if (t === "inlineStr") {
          const is = child(c, "is");
          const s = is === undefined ? "" : unescapeExcel(deepText(is));
          if (s !== "") input = textInput(s);
        } else if (t === "str") {
          if (v !== undefined && v !== "") input = textInput(unescapeExcel(v));
        } else if (t === "b") {
          if (v !== undefined) input = v === "1" || v === "true" ? "TRUE" : "FALSE";
        } else if (t === "e") {
          if (v !== undefined) input = v;
        } else if (t === "d") {
          const n = v === undefined ? null : isoToSerial(v);
          if (n !== null) input = numberInput(n);
        } else if (v !== undefined && v !== "") {
          const n = Number(v);
          input = Number.isFinite(n) ? numberInput(n) : textInput(v);
        }
      }
      const style = c.attrs.s !== undefined ? styles[Number(c.attrs.s)] : undefined;
      if (input === "" && style === undefined) continue;
      if (utf8Length(input) > CELL_INPUT_MAX_BYTES) {
        tooLong = true;
        continue;
      }
      const cell: XCell = { row: at.row, col: at.col, input };
      if (style !== undefined) cell.style = style;
      sheet.cells.push(cell);
      if (at.col > maxCol) maxCol = at.col;
    }
  }
  if (tooLong) skipped.add("8 KB를 넘는 칸");

  for (const col of children(child(root, "cols"), "col")) {
    const min = Number(col.attrs.min) - 1;
    const max = Math.min(Number(col.attrs.max) - 1, Math.max(maxCol + 1, min));
    if (!(min >= 0) || col.attrs.width === undefined) continue;
    const px = col.attrs.hidden === "1" ? COL_WIDTH_MIN : excelColumnPx(Number(col.attrs.width));
    for (let i = min; i <= max && i < 16_384; i++) sheet.colWidths[String(i)] = px;
  }

  const pane = child(child(child(root, "sheetViews"), "sheetView"), "pane");
  if (pane !== undefined && (pane.attrs.state === "frozen" || pane.attrs.state === "frozenSplit")) {
    const rows = Math.trunc(Number(pane.attrs.ySplit ?? 0)) || 0;
    const cols = Math.trunc(Number(pane.attrs.xSplit ?? 0)) || 0;
    sheet.frozenRows = Math.min(FROZEN_ROWS_MAX, Math.max(0, rows));
    sheet.frozenCols = Math.min(FROZEN_COLS_MAX, Math.max(0, cols));
    if (rows > FROZEN_ROWS_MAX || cols > FROZEN_COLS_MAX) skipped.add("너무 큰 틀 고정");
  }

  if (child(root, "mergeCells") !== undefined) skipped.add("셀 병합");
  if (children(root, "conditionalFormatting").length > 0) skipped.add("조건부 서식");
  if (child(root, "dataValidations") !== undefined) skipped.add("데이터 유효성 검사");
  if (child(root, "hyperlinks") !== undefined) skipped.add("하이퍼링크");
  if (child(root, "autoFilter") !== undefined) skipped.add("자동 필터");
  if (child(root, "sheetProtection") !== undefined) skipped.add("시트 보호");
  return sheet;
}

/** Reads an .xlsx file's bytes. Throws XlsxError (or ZipError) with a message for the person. */
export async function readXlsx(bytes: Uint8Array, opts: ReadOptions = {}): Promise<XBook> {
  const parts = new Parts(await readZip(bytes));
  const skipped = new Set<string>();
  const top = rels(parts, "");
  let bookPath: string | undefined;
  for (const r of top.values())
    if (r.type === REL_OFFICE || r.type === REL_STRICT_OFFICE) bookPath = r.target;
  if (bookPath === undefined && parts.has("xl/workbook.xml")) bookPath = "xl/workbook.xml";
  if (bookPath === undefined) throw new XlsxError("엑셀 통합 문서(.xlsx)가 아닙니다");
  const book = parts.xml(bookPath);
  if (book === undefined) throw new XlsxError("통합 문서 부분이 없습니다");
  const bookRels = rels(parts, bookPath);
  const byType = (suffix: string): Rel | undefined =>
    [...bookRels.values()].find((r) => r.type.endsWith("/" + suffix));

  const strings: string[] = [];
  const sst = byType("sharedStrings");
  for (const si of children(sst === undefined ? undefined : parts.xml(sst.target), "si"))
    strings.push(unescapeExcel(deepText(si)));
  const theme = readTheme(parts, byType("theme")?.target);
  const styleRel = byType("styles");
  const styles = readStyles(styleRel === undefined ? undefined : parts.xml(styleRel.target), theme, skipped);

  if (parts.names().some((n) => n.endsWith("vbaProject.bin"))) skipped.add("매크로");
  if (parts.names().some((n) => n.startsWith("xl/pivotTables/") || n.startsWith("xl/pivotCache/")))
    skipped.add("피벗 테이블");
  if (parts.names().some((n) => n.startsWith("xl/charts/"))) skipped.add("차트");
  if (parts.names().some((n) => n.startsWith("xl/media/"))) skipped.add("그림");
  if (parts.names().some((n) => /^xl\/comments\d*\.xml$/.test(n) || n.startsWith("xl/threadedComments/")))
    skipped.add("메모");
  if (parts.names().some((n) => n.startsWith("xl/externalLinks/"))) skipped.add("다른 파일 연결");

  const out: XBook = { sheets: [], names: [], skipped: [] };
  const used = new Set<string>();
  const sheetNames: string[] = [];
  let cells = 0;
  for (const s of children(child(book, "sheets"), "sheet")) {
    const rel = bookRels.get(s.attrs["r:id"] ?? s.attrs.id ?? "");
    sheetNames.push(s.attrs.name ?? "");
    if (rel === undefined || !rel.type.endsWith("/worksheet")) {
      skipped.add(rel?.type.endsWith("/chartsheet") ? "차트 시트" : "워크시트가 아닌 시트");
      continue;
    }
    if (out.sheets.length >= SHEETS_MAX) {
      skipped.add(`${SHEETS_MAX}개 넘는 시트`);
      break;
    }
    let name = (s.attrs.name ?? "").trim();
    if (!sheetNameValid(name) || used.has(name.toLowerCase())) {
      let k = out.sheets.length + 1;
      while (used.has(`시트${k}`.toLowerCase())) k++;
      name = `시트${k}`;
      skipped.add("쓸 수 없는 시트 이름(바꿈)");
    }
    used.add(name.toLowerCase());
    const root = parts.xml(rel.target);
    if (root === undefined) continue;
    const sheet = readSheet(root, name, strings, styles, opts, skipped);
    const sheetRels = rels(parts, rel.target);
    for (const r of sheetRels.values()) {
      if (r.type.endsWith("/drawing")) skipped.add("차트·그림");
      if (r.type.endsWith("/table")) skipped.add("엑셀 표(범위는 값으로)");
    }
    cells += sheet.cells.length;
    if (cells > CELLS_MAX) throw new XlsxError(`칸이 ${CELLS_MAX.toLocaleString()}개를 넘습니다`);
    out.sheets.push(sheet);
  }
  if (out.sheets.length === 0) throw new XlsxError("가져올 워크시트가 없습니다");

  const names = new Set<string>();
  for (const d of children(child(book, "definedNames"), "definedName")) {
    const name = d.attrs.name ?? "";
    if (name.startsWith("_xlnm.") || d.attrs.hidden === "1") continue;
    if (d.attrs.localSheetId !== undefined) {
      const owner = sheetNames[Number(d.attrs.localSheetId)];
      if (names.has(name.toLowerCase()) || owner === undefined) {
        skipped.add("시트 범위 이름 정의 일부");
        continue;
      }
    }
    const formula = fromFileFormula(d.text.trim().replace(/^=/, ""));
    if (!definedNameValid(name) || formula === "" || names.has(name.toLowerCase())) {
      skipped.add("쓸 수 없는 이름 정의");
      continue;
    }
    if (out.names.length >= NAMES_MAX) {
      skipped.add(`${NAMES_MAX}개 넘는 이름 정의`);
      break;
    }
    names.add(name.toLowerCase());
    const xn: XName = { name, formula };
    out.names.push(xn);
  }
  out.skipped = [...skipped];
  return out;
}
